import React, { useEffect, useMemo, useState } from 'react';
import { App, Button, Card, Col, Form, Input, Popconfirm, Row, Select, Switch, Table, Tag, Typography } from 'antd';
import { EditOutlined, FilterOutlined, PlusOutlined } from '@ant-design/icons';
import { useMsal } from '@azure/msal-react';
import { scopes } from '../../authConfig';
import { GetAccessToken } from '../../utils/auth-utils';
import {
  HANDOVER_TO_OTHER_SYSTEM_OUTCOME_KEY,
  HandoverSystemDto,
  LOST_REASON_OUTCOME_KEYS,
  LOST_REASON_OUTCOME_LABELS,
  LostReasonChannelConfigurationAdminDto,
  LostReasonChannelGroupAdminDto,
  LostReasonDto,
  LostReasonOutcomeKey,
} from '../../dto/lost-reason-models';
import { SalesChannel } from '../../dto/model';
import { salesChannelLabel } from '../../dto/qualifying-flow-models';
import { strings } from '../../lang';
import {
  getAvailableLostReasonsForChannel,
  permanentDeleteLostReasonChannelConfiguration,
  permanentDeleteLostReasonChannelGroup,
  saveLostReasonChannelConfiguration,
  saveLostReasonChannelGroup,
  setLostReasonChannelConfigurationActive,
  setLostReasonChannelGroupActive,
} from '../../services/lost-reason-service';

const { Title, Text } = Typography;
const { TextArea } = Input;
const CONFIG_DESCRIPTION_MAX_LENGTH = 2000;

const CHANNEL_FILTER_OPTIONS = Object.values(SalesChannel)
  .filter((c) => c !== SalesChannel.MissingQueue)
  .map((c) => ({ value: c, label: salesChannelLabel[c] }));

function formatOutcomeKeyLabel(key: string): string {
  if (LOST_REASON_OUTCOME_KEYS.some((k) => k === key)) return LOST_REASON_OUTCOME_LABELS[key as LostReasonOutcomeKey];
  return key;
}

function formatBosReasonLabel(reason: LostReasonDto): string {
  return strings.getLanguage() === 'de' ? reason.germanLabel : reason.englishLabel;
}

export type LostReasonGroupsTabProps = {
  configurations: LostReasonChannelConfigurationAdminDto[];
  groups: LostReasonChannelGroupAdminDto[];
  reasonLibrary: LostReasonDto[];
  handoverSystems: HandoverSystemDto[];
  onRefresh: () => Promise<void>;
};

export const LostReasonGroupsTab: React.FC<LostReasonGroupsTabProps> = ({
  configurations,
  groups,
  reasonLibrary,
  handoverSystems,
  onRefresh,
}) => {
  const { instance, inProgress } = useMsal();
  const { notification } = App.useApp();
  const [channelFilter, setChannelFilter] = useState<SalesChannel | undefined>(undefined);

  const [showConfigForm, setShowConfigForm] = useState(false);
  const [editingConfig, setEditingConfig] = useState<LostReasonChannelConfigurationAdminDto | null>(null);
  const [configForm] = Form.useForm();

  const [showGroupForm, setShowGroupForm] = useState(false);
  const [editingGroup, setEditingGroup] = useState<LostReasonChannelGroupAdminDto | null>(null);
  const [availableReasons, setAvailableReasons] = useState<LostReasonDto[]>([]);
  const [groupForm] = Form.useForm();
  const watchedOutcomeKey = Form.useWatch('outcomeKey', groupForm);
  const watchedHandoverKeyword = Form.useWatch('handoverSystemKeyword', groupForm);
  const isHandoverOutcome = watchedOutcomeKey === HANDOVER_TO_OTHER_SYSTEM_OUTCOME_KEY;

  useEffect(() => {
    if (!showGroupForm || !editingGroup) return;

    const fetchAvailableReasons = async () => {
      try {
        const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
        const res = await getAvailableLostReasonsForChannel(
          token.accessToken,
          editingGroup.salesChannel,
          editingGroup.id || null
        );
        if (res.isSuccess && res.data) {
          const currentReasonIds = editingGroup.lostReasons.map(r => r.id).filter(Boolean);
          const currentReasons = editingGroup.lostReasons.filter(r => r.id);
          const available = res.data.filter(r => !currentReasonIds.includes(r.id));
          setAvailableReasons([...currentReasons, ...available]);
        } else {
          setAvailableReasons(editingGroup.lostReasons);
        }
      } catch (error) {
        console.error('Failed to fetch available reasons:', error);
        setAvailableReasons(editingGroup.lostReasons);
      }
    };

    void fetchAvailableReasons();
  }, [showGroupForm, editingGroup, instance, inProgress]);

  const handoverSystemOptions = useMemo(() => {
    const byKeyword = new Map(handoverSystems.map((h) => [h.keyword, h]));
    let pool = handoverSystems.filter((h) => h.isActive);
    const linkedKeyword = watchedHandoverKeyword ?? editingGroup?.handoverSystemKeyword;
    if (linkedKeyword) {
      const linked = byKeyword.get(linkedKeyword);
      if (linked && !pool.some((h) => h.keyword === linkedKeyword)) {
        pool = [...pool, linked];
      } else if (!linked) {
        pool = [
          ...pool,
          {
            id: '',
            keyword: linkedKeyword,
            description: linkedKeyword,
            isActive: false,
          },
        ];
      }
    }
    return [...pool]
      .sort((a, b) => a.description.localeCompare(b.description, undefined, { sensitivity: 'base' }))
      .map((h) => ({
        value: h.keyword,
        label: `${h.description} (${h.keyword})${h.isActive ? '' : ' (inactive)'}`,
      }));
  }, [handoverSystems, editingGroup?.handoverSystemKeyword, watchedHandoverKeyword]);

  const resolveHandoverDisplay = (group: LostReasonChannelGroupAdminDto) => {
    if (group.outcomeKey !== HANDOVER_TO_OTHER_SYSTEM_OUTCOME_KEY) return null;
    if (!group.handoverSystemKeyword) return { text: 'Not set', status: 'warning' as const };
    const label = group.handoverSystemDescription
      ? `${group.handoverSystemDescription} (${group.handoverSystemKeyword})`
      : group.handoverSystemKeyword;
    if (group.handoverSystemIsActive === null || group.handoverSystemIsActive === undefined) {
      return { text: label, status: 'error' as const };
    }
    if (!group.handoverSystemIsActive) {
      return { text: label, status: 'warning' as const };
    }
    return { text: label, status: 'success' as const };
  };

  const reasonOptions = useMemo(
    () =>
      [...availableReasons]
        .sort((a, b) => a.englishLabel.localeCompare(b.englishLabel, undefined, { sensitivity: 'base' }))
        .map((r) => ({
          value: r.id,
          label: `${r.englishLabel} / ${r.germanLabel}`,
        })),
    [availableReasons],
  );

  const channelsWithConfiguration = useMemo(
    () => new Set(configurations.map((c) => c.salesChannel)),
    [configurations],
  );

  const channelOptionsForNewConfiguration = useMemo(
    () => CHANNEL_FILTER_OPTIONS.filter((o) => !channelsWithConfiguration.has(o.value)),
    [channelsWithConfiguration],
  );

  const displayedConfigurations = useMemo(
    () => configurations.filter((c) => !channelFilter || c.salesChannel === channelFilter),
    [configurations, channelFilter],
  );

  const groupsForConfiguration = (configurationId: string) =>
    groups
      .filter((g) => g.configurationId === configurationId)
      .sort((a, b) => {
        const createdDiff = (b.createdDate ?? '').localeCompare(a.createdDate ?? '');
        if (createdDiff !== 0) return createdDiff;
        return b.groupName.localeCompare(a.groupName, undefined, { sensitivity: 'base' });
      });

  const openNewConfig = () => {
    setShowGroupForm(false);
    setEditingGroup(null);
    const initialChannel =
      channelFilter && !channelsWithConfiguration.has(channelFilter)
        ? channelFilter
        : (channelOptionsForNewConfiguration[0]?.value ?? SalesChannel.DigitalSales);
    setEditingConfig({
      id: '',
      salesChannel: initialChannel,
      name: '',
      description: null,
      isActive: false,
    });
    configForm.setFieldsValue({
      salesChannel: initialChannel,
      name: '',
      description: '',
      isActive: false,
    });
    setShowConfigForm(true);
  };

  const openEditConfig = (c: LostReasonChannelConfigurationAdminDto) => {
    setShowGroupForm(false);
    setEditingGroup(null);
    setEditingConfig({ ...c });
    configForm.setFieldsValue({
      salesChannel: c.salesChannel,
      name: c.name,
      description: c.description ?? '',
      isActive: c.isActive,
    });
    setShowConfigForm(true);
  };

  const closeConfigForm = () => {
    setShowConfigForm(false);
    setEditingConfig(null);
  };

  const submitConfig = async () => {
    try {
      const values = await configForm.validateFields();
      const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
      const trimmedDescription = (values.description ?? '').trim();
      const res = await saveLostReasonChannelConfiguration(token.accessToken, {
        id: editingConfig?.id || null,
        salesChannel: values.salesChannel,
        name: values.name,
        description: trimmedDescription === '' ? null : trimmedDescription,
        isActive: values.isActive,
      });
      if (res.isSuccess) {
        notification.success({ message: 'Configuration saved' });
        closeConfigForm();
        await onRefresh();
      } else {
        notification.error({ message: res.errors?.join(', ') ?? 'Save failed' });
      }
    } catch {
    }
  };

  const onDeactivateConfig = async (c: LostReasonChannelConfigurationAdminDto) => {
    if (!c.id) return;
    const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
    const res = await setLostReasonChannelConfigurationActive(token.accessToken, c.id, false);
    if (res.isSuccess) {
      notification.success({ message: 'Configuration deactivated' });
      await onRefresh();
    } else {
      notification.error({ message: (res as any).errors?.join?.(', ') ?? 'Deactivate failed' });
    }
  };

  const onDeleteConfig = async (c: LostReasonChannelConfigurationAdminDto) => {
    if (!c.id) return;
    const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
    const res = await permanentDeleteLostReasonChannelConfiguration(token.accessToken, c.id);
    if (res.isSuccess) {
      notification.success({ message: 'Configuration deleted (outcome groups removed)' });
      await onRefresh();
    } else {
      notification.error({ message: (res as any).errors?.join?.(', ') ?? 'Delete failed' });
    }
  };

  const openNewGroup = (configurationId: string, salesChannel: SalesChannel) => {
    setShowConfigForm(false);
    setEditingConfig(null);
    const cfg = configurations.find((x) => x.id === configurationId);
    setEditingGroup({
      id: '',
      configurationId,
      salesChannel,
      groupName: '',
      outcomeKey: LOST_REASON_OUTCOME_KEYS[0],
      isActive: true,
      lostReasons: [],
      configurationName: cfg?.name ?? '',
      configurationIsActive: cfg?.isActive ?? false,
    });
    groupForm.setFieldsValue({
      configurationId,
      groupName: '',
      outcomeKey: LOST_REASON_OUTCOME_KEYS[0],
      handoverSystemKeyword: undefined,
      isActive: true,
      lostReasonIds: [],
    });
    setShowGroupForm(true);
  };

  const openEditGroup = (g: LostReasonChannelGroupAdminDto) => {
    setShowConfigForm(false);
    setEditingConfig(null);
    setEditingGroup({ ...g });
    groupForm.setFieldsValue({
      configurationId: g.configurationId,
      groupName: g.groupName,
      outcomeKey: g.outcomeKey,
      handoverSystemKeyword: g.handoverSystemKeyword ?? undefined,
      isActive: g.isActive,
      lostReasonIds: g.lostReasons.map((r) => r.id!).filter(Boolean),
    });
    setShowGroupForm(true);
  };

  const closeGroupForm = () => {
    setShowGroupForm(false);
    setEditingGroup(null);
  };

  const submitGroup = async () => {
    try {
      const values = await groupForm.validateFields();
      const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
      const res = await saveLostReasonChannelGroup(token.accessToken, {
        id: editingGroup?.id || null,
        configurationId: String(values.configurationId),
        groupName: values.groupName,
        outcomeKey: values.outcomeKey,
        handoverSystemKeyword:
          values.outcomeKey === HANDOVER_TO_OTHER_SYSTEM_OUTCOME_KEY ? (values.handoverSystemKeyword ?? null) : null,
        isActive: values.isActive,
        lostReasonIds: values.lostReasonIds ?? [],
      });
      if (res.isSuccess) {
        notification.success({ message: 'Outcome group saved' });
        closeGroupForm();
        await onRefresh();
      } else {
        notification.error({ message: res.errors?.join(', ') ?? 'Save failed' });
      }
    } catch {
    }
  };

  const onDeactivateGroup = async (g: LostReasonChannelGroupAdminDto) => {
    if (!g.id) {
      notification.error({ message: 'Cannot deactivate an unsaved group.' });
      return;
    }
    const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
    const res = await setLostReasonChannelGroupActive(token.accessToken, g.id, false);
    if (res.isSuccess) {
      notification.success({ message: 'Outcome group deactivated' });
      await onRefresh();
    } else {
      notification.error({ message: (res as any).errors?.join?.(', ') ?? 'Deactivate failed' });
    }
  };

  const onPermanentDeleteGroup = async (g: LostReasonChannelGroupAdminDto) => {
    if (!g.id) {
      notification.error({ message: 'Cannot delete an unsaved group.' });
      return;
    }
    const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
    const res = await permanentDeleteLostReasonChannelGroup(token.accessToken, g.id);
    if (res.isSuccess) {
      notification.success({ message: 'Outcome group deleted' });
      await onRefresh();
    } else {
      notification.error({ message: (res as any).errors?.join?.(', ') ?? 'Delete failed' });
    }
  };

  const groupColumns = () => [
    {
      title: 'Outcome',
      dataIndex: 'outcomeKey',
      key: 'ok',
      width: '14%',
      className: 'lost-reason-outcome-key-column',
      render: (key: string) => <span className="lost-reason-outcome-cell-text">{formatOutcomeKeyLabel(key)}</span>,
    },
    {
      title: 'Handover system',
      key: 'hs',
      width: '8%',
      className: 'lost-reason-handover-column',
      onCell: () => ({ className: 'lost-reason-handover-cell' }),
      render: (_: unknown, r: LostReasonChannelGroupAdminDto) => {
        const handover = resolveHandoverDisplay(r);
        if (!handover) return '—';
        return (
          <span className="lost-reason-outcome-cell-text">
            {handover.text}{' '}
            {handover.status === 'error' && <Tag color="error">Missing</Tag>}
            {handover.status === 'warning' && <Tag color="warning">{r.handoverSystemKeyword ? 'Inactive' : 'Not set'}</Tag>}
          </span>
        );
      },
    },
    {
      title: 'Label',
      dataIndex: 'groupName',
      key: 'gn',
      width: '8%',
      className: 'lost-reason-label-column',
      onCell: () => ({ className: 'lost-reason-label-cell' }),
      render: (value: string) => <span className="lost-reason-outcome-cell-text">{value}</span>,
    },
    {
      title: 'Reasons',
      key: 'reasons',
      width: '36%',
      className: 'lost-reason-reasons-column',
      onCell: () => ({ className: 'lost-reason-reasons-cell' }),
      render: (_: unknown, r: LostReasonChannelGroupAdminDto) => {
        if (r.lostReasons.length === 0) return '—';
        return (
          <ul className="lost-reason-outcome-reasons-list">
            {[...r.lostReasons]
              .sort((a, b) =>
                formatBosReasonLabel(a).localeCompare(formatBosReasonLabel(b), undefined, { sensitivity: 'base' }),
              )
              .map((reason) => (
                <li key={reason.id ?? `${reason.englishLabel}-${reason.germanLabel}`}>{formatBosReasonLabel(reason)}</li>
              ))}
          </ul>
        );
      },
    },
    {
      title: 'Active',
      dataIndex: 'isActive',
      key: 'a',
      width: 56,
      className: 'lost-reason-active-column',
      onHeaderCell: () => ({ className: 'lost-reason-active-column' }),
      onCell: () => ({ className: 'lost-reason-active-cell' }),
      render: (v: boolean) => (v ? 'Yes' : 'No'),
    },
    {
      title: '',
      key: 'act',
      width: '27%',
      className: 'lost-reason-group-actions-column',
      onCell: () => ({ className: 'lost-reason-group-actions-cell' }),
      render: (_: unknown, r: LostReasonChannelGroupAdminDto) => (
        <div className="lost-reason-group-actions">
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => openEditGroup(r)}
            disabled={showGroupForm && editingGroup?.id !== r.id}
          >
            Edit
          </Button>
          {r.isActive && (
            <Button size="small" onClick={() => void onDeactivateGroup(r)}>
              Deactivate
            </Button>
          )}
          <Popconfirm
            title="Delete outcome group?"
            description="Removes this outcome mapping only."
            okText="Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
            onConfirm={() => void onPermanentDeleteGroup(r)}
          >
            <Button size="small" danger>
              Delete
            </Button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  const renderConfigForm = (isNew: boolean) => (
    <div style={{ marginBottom: 16, padding: '16px 0', borderTop: isNew ? undefined : '1px solid #f0f0f0' }}>
      <Title level={5} style={{ marginTop: 0 }}>
        {isNew ? 'Add Sales Channel Configuration' : 'Edit Sales Channel Configuration'}
      </Title>
      <Form form={configForm} layout="vertical" style={{ maxWidth: 480 }}>
        <Form.Item name="salesChannel" label="Sales channel" rules={[{ required: true }]}>
          <Select
            options={editingConfig?.id ? CHANNEL_FILTER_OPTIONS : channelOptionsForNewConfiguration}
            disabled={!!editingConfig?.id}
          />
        </Form.Item>
        <Form.Item name="name" label="Configuration name" rules={[{ required: true }]}>
          <Input placeholder="e.g. Digital — Q2 2026" />
        </Form.Item>
        <Form.Item
          name="description"
          label="Description (optional)"
          rules={[
            {
              max: CONFIG_DESCRIPTION_MAX_LENGTH,
              message: `Keep the description under ${CONFIG_DESCRIPTION_MAX_LENGTH} characters.`,
            },
          ]}
        >
          <div>
            <div style={{ marginBottom: 4, color: 'var(--text-tertiary)', fontSize: '14px' }}>
              Shown to operators alongside this channel's configuration; leave blank if no extra guidance is needed.
            </div>
            <TextArea
              rows={3}
              autoSize={{ minRows: 3, maxRows: 8 }}
              maxLength={CONFIG_DESCRIPTION_MAX_LENGTH}
              showCount
              placeholder="e.g. Use these outcomes when handling Q2 2026 Digital Sales bookings."
            />
          </div>
        </Form.Item>
        <Form.Item
          name="isActive"
          label="Active for booking"
          valuePropName="checked"
          extra="Only one configuration exists per sales channel; inactive means booking will not offer configurable reasons for that channel."
        >
          <Switch />
        </Form.Item>
        <Button type="primary" onClick={() => void submitConfig()}>
          Save
        </Button>
        <Button style={{ marginLeft: 8 }} onClick={closeConfigForm}>
          Cancel
        </Button>
      </Form>
    </div>
  );

  const renderGroupForm = () => (
    <div style={{ padding: '8px 0' }}>
      <Title level={5} style={{ marginTop: 0 }}>
        {editingGroup?.id ? 'Edit outcome' : 'Add outcome'}
      </Title>
      <Form form={groupForm} layout="vertical" style={{ maxWidth: 560 }}>
        <Form.Item name="configurationId" hidden>
          <Input type="hidden" />
        </Form.Item>
        <Form.Item name="groupName" label="Outcome label (internal)" rules={[{ required: true }]}>
          <Input placeholder="Shown in admin lists" />
        </Form.Item>
        <Form.Item name="outcomeKey" label="Outcome (drives close-case behavior)" rules={[{ required: true }]}>
          <Select
            showSearch
            optionFilterProp="label"
            options={LOST_REASON_OUTCOME_KEYS.map((k) => ({
              value: k,
              label: LOST_REASON_OUTCOME_LABELS[k],
            }))}
          />
        </Form.Item>
        {isHandoverOutcome && (
          <Form.Item
            name="handoverSystemKeyword"
            label="Handover system"
            rules={[{ required: true, message: 'Select the system this group hands the lead over to.' }]}
            extra="The selected system's keyword is forwarded to Lead Handling, and its name is shown to the booker."
          >
            <Select
              showSearch
              optionFilterProp="label"
              options={handoverSystemOptions}
              placeholder="Select a handover system"
              notFoundContent="No active handover systems. Add one in the Handover systems tab."
            />
          </Form.Item>
        )}
        <Form.Item name="isActive" label="Active" valuePropName="checked">
          <Switch />
        </Form.Item>
        <Form.Item
          name="lostReasonIds"
          label="BOS lost reasons for this outcome"
          rules={[
            {
              type: 'array',
              min: 1,
              required: true,
              message: 'Select at least one BOS lost reason.',
            },
          ]}
        >
          <Select mode="multiple" allowClear optionFilterProp="label" options={reasonOptions} placeholder="Select reasons" />
        </Form.Item>
        <Button type="primary" onClick={() => void submitGroup()}>
          Save
        </Button>
        <Button style={{ marginLeft: 8 }} onClick={closeGroupForm}>
          Cancel
        </Button>
      </Form>
    </div>
  );

  const isEditingConfig = (c: LostReasonChannelConfigurationAdminDto) =>
    showConfigForm && editingConfig != null && editingConfig.id === c.id;

  const isAddingGroupToConfig = (configurationId: string) =>
    showGroupForm && editingGroup != null && !editingGroup.id && editingGroup.configurationId === configurationId;

  const expandedGroupRowKeys =
    showGroupForm && editingGroup?.id ? [editingGroup.id] : [];

  return (
    <div
      className="container flex-admin-vertical-container"
      style={{ width: '100%', maxWidth: 1100, minWidth: 0, justifyContent: 'flex-start' }}
    >
      <Row style={{ marginBottom: 12, flexDirection: 'column' }}>
        <Title level={4} style={{ marginBottom: 0 }}>
          Lost reason configurations (per sales channel)
        </Title>
        <Text type="secondary">
          At most one configuration per sales channel. Within it, each outcome maps to one or more BOS reasons; the same reason cannot appear under two
          outcomes. Handover to other system may be used more than once per channel when each group targets a different handover system. Use default legacy
          behavior for lead queue; other outcomes are not supported yet.
        </Text>
      </Row>

      <Row gutter={[16, 12]} style={{ marginBottom: 16 }} align="middle" wrap className="lost-reason-groups-toolbar">
        <Col>
          <FilterOutlined style={{ marginRight: 8 }} />
          <Select
            allowClear
            placeholder="Filter by sales channel"
            style={{ width: 260 }}
            options={CHANNEL_FILTER_OPTIONS}
            value={channelFilter}
            onChange={(v) => setChannelFilter(v)}
          />
        </Col>
        <Col flex={1} />
        <Col>
          <Button type="primary" icon={<PlusOutlined />} onClick={openNewConfig} disabled={showConfigForm || showGroupForm}>
            Add Sales Channel Configuration
          </Button>
        </Col>
      </Row>

      {showConfigForm && editingConfig && !editingConfig.id ? (
        <Card className="lost-reason-channel-card lost-reason-channel-card--new">{renderConfigForm(true)}</Card>
      ) : null}

      {displayedConfigurations.map((c) => (
        <Card
          key={c.id ?? c.name}
          className="lost-reason-channel-card"
          title={
            <span>
              {c.name}{' '}
              <Tag color={c.isActive ? 'green' : 'default'}>{c.isActive ? 'Active for booking' : 'Inactive'}</Tag>
              <Tag color="blue" style={{ marginLeft: 8 }}>
                {salesChannelLabel[c.salesChannel] ?? c.salesChannel}
              </Tag>
            </span>
          }
          extra={
            <div className="lost-reason-channel-card-actions">
              <Button
                size="small"
                icon={<EditOutlined />}
                onClick={() => openEditConfig(c)}
                disabled={(showConfigForm && !isEditingConfig(c)) || showGroupForm}
              >
                Edit Sales Channel Configuration
              </Button>
              {c.isActive && c.id && (
                <Button size="small" onClick={() => void onDeactivateConfig(c)} disabled={showConfigForm || showGroupForm}>
                  Deactivate
                </Button>
              )}
              {c.id && (
                <Popconfirm
                  title="Delete configuration?"
                  description="Deletes all outcome groups in this configuration."
                  okText="Delete"
                  cancelText="Cancel"
                  okButtonProps={{ danger: true }}
                  onConfirm={() => void onDeleteConfig(c)}
                >
                  <Button size="small" danger disabled={showConfigForm || showGroupForm}>
                    Delete
                  </Button>
                </Popconfirm>
              )}
              {c.id && (
                <div className="lost-reason-add-outcome-button-row">
                  <Button
                    type="primary"
                    size="small"
                    icon={<PlusOutlined />}
                    onClick={() => openNewGroup(c.id!, c.salesChannel)}
                    disabled={showConfigForm || showGroupForm}
                  >
                    Add outcome
                  </Button>
                </div>
              )}
            </div>
          }
        >
          {isEditingConfig(c) ? renderConfigForm(false) : null}
          {!isEditingConfig(c) && c.description ? (
            <Text type="secondary" style={{ display: 'block', whiteSpace: 'pre-wrap', marginBottom: 12 }}>
              {c.description}
            </Text>
          ) : null}
          {!isEditingConfig(c) && !c.id ? (
            <Text type="warning">Save the configuration first, then add outcomes.</Text>
          ) : null}
          {!isEditingConfig(c) && c.id ? (
            <>
              <Table
                className="lost-reason-outcome-table"
                rowKey="id"
                size="small"
                pagination={false}
                tableLayout="fixed"
                columns={groupColumns()}
                dataSource={groupsForConfiguration(c.id)}
                expandable={{
                  expandedRowKeys: editingGroup?.configurationId === c.id ? expandedGroupRowKeys : [],
                  expandedRowRender: () => renderGroupForm(),
                  showExpandColumn: false,
                }}
              />
              {isAddingGroupToConfig(c.id) ? (
                <div className="lost-reason-channel-form-panel">
                  {renderGroupForm()}
                </div>
              ) : null}
            </>
          ) : null}
        </Card>
      ))}

      {displayedConfigurations.length === 0 && (
        <Text type="secondary">No configurations for this filter. Add one to get started.</Text>
      )}
    </div>
  );
};
